import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Analytics } from '@jfw/angular/shared/firebase/analytics';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { RouterModule } from '@angular/router';
import { SpinnerModule } from '@jfw/angular/ecommerce/common-components/spinner';
import { AccountService } from '@jfw/angular/shared/services';
import { StickyStackTopDirective } from '@jfw/angular/shared/utils-angular';
import { combineLatest, map, tap } from 'rxjs';
import { environment } from '../environments/environment';
import { AccountInfoDrawerComponent } from './standalone/components/account-manager/account-info-drawer/account-info-drawer.component';
import { AccountBannerComponent } from './standalone/components/account-manager/account-banner/account-banner.component';
import { AccountSetterComponent } from './standalone/components/account-manager/account-setter/account-setter.component';
import { HeaderComponent } from './standalone/components/header/header.component';
import { ModuleHeaderComponent } from './standalone/components/module-header/module-header.component';
import { NavigationComponent } from './standalone/components/navigation/navigation.component';
import { PageViewTrackingService } from './standalone/services/analytics/page-view-tracking.service';
import { AuthService } from './standalone/services/auth/auth.service';

// trigger deploy 89

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    HeaderComponent,
    MatSidenavModule,
    NavigationComponent,
    AccountBannerComponent,
    AccountSetterComponent,
    AccountInfoDrawerComponent,
    ModuleHeaderComponent,
    RouterModule,
    SpinnerModule,
    CommonModule,
    StickyStackTopDirective,
  ],
})
export class AppComponent implements OnInit {
  @ViewChild('drawer') drawer!: MatDrawer;
  private analytics = inject(Analytics); // needed for GA
  private pageViewTracking = inject(PageViewTrackingService); // EXT-5255: explicit page_view firing
  private authService = inject(AuthService);
  private accountService = inject(AccountService);

  public enablePlatformAccountSetter = environment.enablePlatformAccountSetter;

  private static readonly MIN_DRAWER_WIDTH_PX = 320;
  private static readonly MAX_DRAWER_WIDTH_RATIO = 0.8;
  // EXT-6557: wide enough that the Shipping tab's tables don't wrap or
  // truncate; matches the 461px default in app.component.scss.
  private static readonly DEFAULT_DRAWER_WIDTH_PX = 461;
  // EXT-6557: the width the user last dragged the drawer to, per browser.
  static readonly DRAWER_WIDTH_STORAGE_KEY = 'accountDrawerWidthPx';

  drawerWidthPx = signal<number>(AppComponent.loadDrawerWidth());

  private onDrawerResizeMove = (_event: MouseEvent) => {};
  private onDrawerResizeEnd = () => {};

  private static clampDrawerWidth(width: number): number {
    const maxWidth = window.innerWidth * AppComponent.MAX_DRAWER_WIDTH_RATIO;
    return Math.min(
      maxWidth,
      Math.max(AppComponent.MIN_DRAWER_WIDTH_PX, width),
    );
  }

  private static loadDrawerWidth(): number {
    try {
      const stored = Number(
        localStorage.getItem(AppComponent.DRAWER_WIDTH_STORAGE_KEY),
      );
      if (stored > 0) {
        return AppComponent.clampDrawerWidth(stored);
      }
    } catch {
      // Storage blocked (private window etc.) -- fall back to the default.
    }
    return AppComponent.DEFAULT_DRAWER_WIDTH_PX;
  }

  startDrawerResize(event: MouseEvent): void {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = this.drawerWidthPx();
    document.body.style.userSelect = 'none';

    this.onDrawerResizeMove = (moveEvent: MouseEvent) => {
      // The drawer is anchored to the right edge (position="end"), so dragging
      // the handle on its left edge leftward should grow the drawer.
      const delta = startX - moveEvent.clientX;
      this.drawerWidthPx.set(AppComponent.clampDrawerWidth(startWidth + delta));
    };

    this.onDrawerResizeEnd = () => {
      document.body.style.userSelect = '';
      try {
        localStorage.setItem(
          AppComponent.DRAWER_WIDTH_STORAGE_KEY,
          String(Math.round(this.drawerWidthPx())),
        );
      } catch {
        // Storage blocked -- the width just isn't remembered.
      }
      document.removeEventListener('mousemove', this.onDrawerResizeMove);
      document.removeEventListener('mouseup', this.onDrawerResizeEnd);
    };

    document.addEventListener('mousemove', this.onDrawerResizeMove);
    document.addEventListener('mouseup', this.onDrawerResizeEnd);
  }

  // execute key auth observables to ensure that
  // initial values are loaded across the app

  public vm$ = combineLatest([
    this.authService.firebaseUser$,
    this.authService.platformUser$,
    this.authService.moduleTabs$,
    this.authService.currentModule$,
  ]).pipe(
    map(([firebaseUser, portalUser]) => ({
      firebaseUser,
      portalUser,
    })),
    tap(({ portalUser }) => {
      if (!portalUser && this.drawer) {
        this.drawer.close();
      }
    }),
  );

  // Real, sequential readiness flags — not a decorative animation — so the loading
  // screen (app.component.html's vm$ @else branch) can name which actual step is
  // taking a while, rather than showing a single unexplained spinner for the whole
  // combineLatest wait. Mirrors the true dependency chain in auth.service.ts:
  // firebaseUser$ → platformUser$ → moduleTabs$/currentModule$.
  private static readonly LOADING_STEPS = [
    'Signing you in…',
    'Fetching your profile…',
    'Loading your workspace…',
  ] as const;

  private readonly authReady = toSignal(
    this.authService.firebaseUser$.pipe(map(() => true)),
    { initialValue: false },
  );
  private readonly profileReady = toSignal(
    this.authService.platformUser$.pipe(map(() => true)),
    { initialValue: false },
  );
  private readonly workspaceReady = toSignal(
    combineLatest([
      this.authService.moduleTabs$,
      this.authService.currentModule$,
    ]).pipe(map(() => true)),
    { initialValue: false },
  );

  protected readonly loadingStepIndex = computed(() => {
    if (!this.authReady()) return 0;
    if (!this.profileReady()) return 1;
    if (!this.workspaceReady()) return 2;
    return 3;
  });

  // loadingStepIndex can reach 3 (all steps done) right before vm$ itself resolves
  // and this @else branch stops being rendered at all — clamp so the lookup stays
  // in bounds during that brief instant rather than relying on render timing.
  protected readonly loadingLabel = computed(() => {
    const index = Math.min(this.loadingStepIndex(), 2);
    return AppComponent.LOADING_STEPS[index];
  });

  protected readonly loadingSteps = AppComponent.LOADING_STEPS;

  ngOnInit(): void {
    console.log(`envVersion: ${environment.envVersion}`);
  }

  showDrawer() {
    return this.accountService.showDrawer();
  }
}
